import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LogOut, LayoutGrid, Settings as SettingsIcon, ExternalLink } from "lucide-react";
import { clearToken } from "@/lib/api";

export default function AdminLayout({ children, title, subtitle, actions }) {
  const nav = useNavigate();
  const loc = useLocation();

  const logout = () => {
    clearToken();
    nav("/admin/login", { replace: true });
  };

  const navItem = (to, Icon, label) => {
    const active = loc.pathname === to || (to === "/admin" && loc.pathname.startsWith("/admin/q/"));
    return (
      <Link
        to={to}
        data-testid={`adminnav-${label.toLowerCase()}`}
        className={`flex items-center gap-3 px-4 py-3 text-sm uppercase tracking-widest font-mono transition-colors border-l-2 ${
          active
            ? "border-[#E63946] bg-zinc-100 text-zinc-900"
            : "border-transparent text-zinc-500 hover:text-zinc-900 hover:bg-zinc-50"
        }`}
      >
        <Icon size={14} />
        {label}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-900 flex">
      {/* Sidebar */}
      <aside className="w-64 border-r border-zinc-200 bg-white flex flex-col">
        <div className="p-6 border-b border-zinc-200">
          <Link to="/admin" className="flex items-center gap-2">
            <span className="relative inline-flex w-8 h-8 items-center justify-center bg-[#E63946]">
              <span className="font-heading font-black text-white text-lg leading-none">M</span>
              <span className="absolute -bottom-1 -right-1 w-2 h-2 bg-zinc-900"></span>
            </span>
            <div>
              <div className="font-heading font-black tracking-tighter text-lg leading-none text-zinc-900">
                Motorie<span className="text-[#E63946]">Ricambi</span>
              </div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 mt-1">
                Admin Console
              </div>
            </div>
          </Link>
        </div>

        <nav className="flex-1 py-4">
          {navItem("/admin", LayoutGrid, "Richieste")}
          {navItem("/admin/settings", SettingsIcon, "Impostazioni")}
        </nav>

        <div className="p-4 border-t border-zinc-200 space-y-2">
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between px-3 py-2 text-xs uppercase tracking-widest font-mono text-zinc-500 hover:text-zinc-900 transition-colors"
          >
            Vedi Sito Pubblico
            <ExternalLink size={12} />
          </a>
          <button
            onClick={logout}
            data-testid="admin-logout-btn"
            className="w-full flex items-center justify-between px-3 py-2 text-xs uppercase tracking-widest font-mono text-zinc-500 hover:text-[#E63946] transition-colors"
          >
            Esci
            <LogOut size={12} />
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 overflow-hidden">
        <header className="border-b border-zinc-200 bg-white px-8 py-6 flex items-end justify-between gap-4">
          <div>
            {subtitle && (
              <div className="flex items-center gap-3 mb-1">
                <div className="w-6 h-px bg-[#E63946]" />
                <span className="text-[10px] uppercase tracking-[0.3em] font-mono text-[#E63946]">
                  {subtitle}
                </span>
              </div>
            )}
            <h1 className="font-heading font-black text-3xl tracking-tighter text-zinc-900">
              {title}
            </h1>
          </div>
          <div className="flex items-center gap-3">{actions}</div>
        </header>
        <div className="p-8 overflow-x-auto">{children}</div>
      </main>
    </div>
  );
}
